import { useMemo, useState } from 'react';
import type { ProjectTaskGroup } from '../../../lib/dailyApi';
import { dailyTaskRow, TaskListRow } from '../../tasks/TaskListRow';
import { useI18n } from '../../../contexts/I18nContext';
import { Select } from '../../ui/Select';

interface Props {
  byProject: ProjectTaskGroup[];
  statuses: string[];
  total: number;
}

const ALL = '__all__';

/**
 * All active tasks for a person, grouped by project (CLE-21). Includes a
 * status filter and a project filter; both default to "all". Tasks arrive
 * pre-sorted by priority then due date from the backend; `task.status` is the
 * configurable status's display name.
 */
export function AllTasksByProjectSection({ byProject, statuses, total }: Props) {
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [projectFilter, setProjectFilter] = useState<string>(ALL);

  // Stable key for a project group (id, or name when there is no project).
  const groupKey = (g: ProjectTaskGroup): string => g.projectId ?? `name:${g.projectName}`;

  const filtered = useMemo(() => {
    return byProject
      .filter((group) => projectFilter === ALL || groupKey(group) === projectFilter)
      .map((group) => ({
        ...group,
        tasks: group.tasks.filter((task) => statusFilter === ALL || task.status === statusFilter),
      }))
      .filter((group) => group.tasks.length > 0);
  }, [byProject, statusFilter, projectFilter]);

  const visibleCount = filtered.reduce((sum, g) => sum + g.tasks.length, 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-gray-800">
          {t('daily.allTasks')} <span className="text-sm font-normal text-gray-500">({total})</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-600">{t('common.status')}</span>
            <Select
              label={t('common.status')}
              hideLabel
              size="sm"
              clearable
              value={statusFilter === ALL ? '' : statusFilter}
              onChange={(v) => setStatusFilter(v || ALL)}
              placeholder={t('common.all')}
              options={statuses.map((s) => ({ value: s, label: s }))}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-600">{t('daily.filter.project')}</span>
            <Select
              label={t('daily.filter.project')}
              hideLabel
              size="sm"
              clearable
              value={projectFilter === ALL ? '' : projectFilter}
              onChange={(v) => setProjectFilter(v || ALL)}
              placeholder={t('common.all')}
              options={byProject.map((group) => ({
                value: groupKey(group),
                label: group.projectName,
              }))}
              className="w-48"
            />
          </div>
        </div>
      </div>

      {total === 0 ? (
        <p className="text-sm text-gray-500">{t('daily.allTasks.empty')}</p>
      ) : visibleCount === 0 ? (
        <p className="text-sm text-gray-500">{t('daily.allTasks.filtered')}</p>
      ) : (
        <div className="space-y-4">
          {filtered.map((group) => (
            <div key={groupKey(group)}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                {group.projectName} <span className="text-gray-400">({group.tasks.length})</span>
              </h3>
              <ul className="mt-2 overflow-hidden rounded-md border border-gray-200">
                {group.tasks.map((task) => (
                  <TaskListRow key={task.id} task={dailyTaskRow(task)} newTab />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
